import {
  FunctionComponent,
  PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from 'react';

import { ChoosePlanProviderContainer } from '../components/ChoosePlanDrawer/ChoosePlanDrawer.context';

import { AppProvider } from './App.context.base';
import {
  SESSION_STATE,
  SESSION_STATE_RETRY_DELAY,
} from './App.context.constants';
import {
  buildSessionLoginUrl,
  buildSessionLogoutUrl,
  fetchSessionState,
} from './App.context.helpers';
import {
  useIframeContext,
  useSellerSelection,
  useSessionLossCheck,
} from './App.context.hooks';
import {
  AppLoginArgs,
  AppLogoutArgs,
  SessionSnapshot,
} from './App.context.types';

export const SessionAppContextProvider: FunctionComponent<
  PropsWithChildren
> = ({ children }) => {
  const { selectedSeller, onSelectSeller } = useSellerSelection();
  const { isIframeContext, onIframePageMount, onIframePageUnmount } =
    useIframeContext();

  const [session, setSession] = useState<SessionSnapshot | undefined>(
    undefined,
  );

  useEffect(() => {
    let isSubscribed = true;
    let retryTimeoutId: number | undefined;

    const resolveSessionState = async (): Promise<void> => {
      const snapshot = await fetchSessionState();

      if (!isSubscribed) {
        return;
      }

      setSession(snapshot);

      if (snapshot.state === SESSION_STATE.UNAVAILABLE) {
        retryTimeoutId = window.setTimeout(
          resolveSessionState,
          SESSION_STATE_RETRY_DELAY,
        );
      }
    };

    resolveSessionState();

    return () => {
      isSubscribed = false;
      window.clearTimeout(retryTimeoutId);
    };
  }, []);

  useSessionLossCheck(setSession);

  const login = useCallback((args?: AppLoginArgs) => {
    window.location.assign(buildSessionLoginUrl(args));
  }, []);

  const logout = useCallback((args?: AppLogoutArgs) => {
    window.location.assign(buildSessionLogoutUrl(args));
  }, []);

  return (
    <AppProvider
      value={{
        isIframeContext,
        onIframePageMount,
        onIframePageUnmount,
        selectedSeller,
        onSelectSeller,
        isAuthenticated: session?.state === SESSION_STATE.ACTIVE,
        sessionState: session?.state,
        clientId: session?.clientId,
        authority: session?.authority,
        login,
        logout,
      }}
    >
      <ChoosePlanProviderContainer>{children}</ChoosePlanProviderContainer>
    </AppProvider>
  );
};
